import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { auditService } from '../services/auditService';
import { useLiveEvents } from '../utils/liveEvents';
import { PageLoader } from './Loader';
import './ScheduleKpis.css';

const fmt = (value) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 3 });
const POLL_MS = 60000;

// ---- Icons (stroke icons, 24px grid) ----------------------------------------

const paths = {
  phone: <><rect x="6" y="2.5" width="12" height="19" rx="2.5" /><path d="M11 18.5h2" /></>,
  barcode: <path d="M4 5v14M7 5v14M10 5v14M13 5v14M15.5 5v14M18 5v14M20.5 5v14" />,
  pin: <><path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></>,
  check: <><circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.7 2.7L16 9.8" /></>,
  alert: <><path d="M10.3 3.9L2.6 17.5A2 2 0 0 0 4.3 20.5h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9.5v4M12 17h.01" /></>,
  nobarcode: <><circle cx="12" cy="12" r="9" /><path d="M5.6 5.6l12.8 12.8" /></>,
  damaged: <><path d="M21 8l-9-5-9 5v8l9 5 9-5z" /><path d="M3 8l9 5 9-5M12 13v8" /><path d="M15 15.5l3 3M18 15.5l-3 3" /></>,
  sheet: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 12.5h6M9 16h6M12 12.5V19" /></>,
  signal: <><circle cx="12" cy="12" r="1.8" /><path d="M8.5 8.5a5 5 0 0 0 0 7M15.5 8.5a5 5 0 0 1 0 7M5.6 5.6a9 9 0 0 0 0 12.8M18.4 5.6a9 9 0 0 1 0 12.8" /></>,
  building: <><rect x="4" y="3" width="11" height="18" rx="1.5" /><path d="M15 9h4a1 1 0 0 1 1 1v11M8 7h3M8 11h3M8 15h3M3 21h18" /></>,
  store: <><path d="M4 9l1.5-5h13L20 9" /><path d="M4 9a2.7 2.7 0 0 0 5.3 0 2.7 2.7 0 0 0 5.4 0 2.7 2.7 0 0 0 5.3 0" /><path d="M5.5 11.5V20h13v-8.5M10 20v-5h4v5" /></>,
  calendarCheck: <><rect x="3.5" y="5" width="17" height="15.5" rx="2" /><path d="M8 3v4M16 3v4M3.5 10h17M9 15l2 2 4-4" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  location: <><path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></>,
};

function Icon({ name, size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

// ---- Cards ------------------------------------------------------------------

/**
 * A row of headline stat cards (same look as the schedule KPIs), for list
 * pages such as Organizations and Outlets.
 * items: [{ label, icon, tone, value, valueTone, note, extra, title }]
 */
export function StatCards({ items, className = '' }) {
  return (
    <div className={`kpi-grid stat-cards ${className}`}>
      {items.map((item) => (
        <Card key={item.label} {...item} />
      ))}
    </div>
  );
}

function Card({ label, icon, tone, value, valueTone, note, extra, onClick, title }) {
  const body = (
    <>
      <span className="kpi-card__top">
        <span className="kpi-card__label">{label}</span>
        <span className={`kpi-card__icon kpi-tone--${tone}`}>
          <Icon name={icon} />
        </span>
      </span>
      <strong className={`kpi-card__value${valueTone ? ` kpi-tone--${valueTone}` : ''}`}>{value}</strong>
      <span className={`kpi-card__note kpi-tone--${tone}`}>{note}</span>
      {extra ? <span className="kpi-card__extra">{extra}</span> : null}
    </>
  );
  return onClick ? (
    <button type="button" className="kpi-card kpi-card--link" onClick={onClick} title={title}>
      {body}
    </button>
  ) : (
    <article className="kpi-card" title={title}>
      {body}
    </article>
  );
}

/**
 * The eight headline cards. onOpen(tab) makes them clickable (portal tabs:
 * scans, locations, validean, adjustments, compare).
 */
export function KpiCards({ kpis, progress, onOpen }) {
  if (!kpis) return null;
  const { devices, scans, locations, adjustments, book } = kpis;
  const open = (tab) => (onOpen ? () => onOpen(tab) : undefined);
  const verified =
    progress?.basis === 'book' && progress.percent != null ? `${progress.percent}% of book verified` : '';

  return (
    <div className="kpi-grid">
      <Card
        label="HHT fleet status"
        icon="phone"
        tone="good"
        value={devices.total ? `${fmt(devices.active)} / ${fmt(devices.total)}` : '—'}
        note={devices.total ? 'Active scanners' : 'No handheld has synced yet'}
        extra={devices.total ? `Uploaded in the last ${devices.active_minutes} min` : ''}
        title="Handhelds that uploaded scans to this schedule in the last 15 minutes / all handhelds that synced to it"
        onClick={open('scans')}
      />
      <Card
        label="Physical scanned"
        icon="barcode"
        tone="muted"
        value={fmt(scans.scanned_qty)}
        note="Total units captured"
        extra={`${fmt(scans.scans)} scans${verified ? ` · ${verified}` : ''}`}
        onClick={open('scans')}
      />
      <Card
        label="Location counted"
        icon="pin"
        tone="info"
        value={locations.has_master ? `${fmt(locations.counted)} / ${fmt(locations.total)}` : fmt(locations.counted)}
        note={
          locations.has_master
            ? `(${locations.percent ?? 0}%) Locations completed`
            : 'Locations scanned · no location master'
        }
        onClick={open('locations')}
      />
      <Card
        label="Valid count"
        icon="check"
        tone="good"
        value={fmt(scans.valid_qty)}
        note="Matched with AMD"
        extra={scans.resolved_scans ? `${fmt(scans.resolved_scans)} invalid scans resolved` : ''}
        onClick={open('validean')}
      />
      <Card
        label="Invalid count"
        icon="alert"
        tone="bad"
        valueTone={scans.invalid_qty ? 'bad' : ''}
        value={fmt(scans.invalid_qty)}
        note={`${fmt(scans.invalid_eans)} unmapped EAN${scans.invalid_eans === 1 ? '' : 's'}`}
        onClick={open('validean')}
      />
      <Card
        label="WBC counted"
        icon="nobarcode"
        tone="warn"
        valueTone={adjustments.wbc.qty ? 'warn' : ''}
        value={fmt(adjustments.wbc.qty)}
        note="Without barcode (WBC)"
        extra={adjustments.wbc.rows ? `${fmt(adjustments.wbc.rows)} lines · Manual adjustments` : 'Manual adjustments'}
        onClick={open('adjustments')}
      />
      <Card
        label="Damage stock"
        icon="damaged"
        tone="bad"
        valueTone={adjustments.damaged.qty ? 'bad' : ''}
        value={fmt(adjustments.damaged.qty)}
        note="Flagged as damaged"
        extra={adjustments.damaged.rows ? `${fmt(adjustments.damaged.rows)} lines` : ''}
        onClick={open('adjustments')}
      />
      <Card
        label="Stock upload (Excel)"
        icon="sheet"
        tone={book.uploaded ? 'teal' : 'muted'}
        value={book.uploaded ? 'Uploaded' : 'Pending'}
        valueTone={book.uploaded ? '' : 'muted'}
        note={book.uploaded ? `${fmt(book.qty)} units loaded` : 'Book stock not uploaded yet'}
        extra={book.uploaded ? `${fmt(book.eans)} EANs · ${book.filename || 'book stock'}` : ''}
        onClick={open('compare')}
      />
    </div>
  );
}

const STATUS = {
  in_progress: { label: 'In-Progress', tone: 'live' },
  not_started: { label: 'Not started yet', tone: 'idle' },
  closed: { label: 'Closed', tone: 'idle' },
};

/** Header: schedule number, client, outlet, lead and the live status pill. */
export function KpiHeader({ kpis, children }) {
  const s = kpis.schedule;
  const active = kpis.devices.active > 0;
  const status = STATUS[s.schedule_status] || STATUS.in_progress;
  return (
    <header className="kpi-head">
      <span className={`kpi-head__badge${active ? ' kpi-head__badge--live' : ''}`}>
        <Icon name="signal" size={24} />
      </span>
      <div className="kpi-head__text">
        <p className="kpi-head__eyebrow">
          Target audit schedule
          <code>{s.schedule_no || `#${s.id}`}</code>
        </p>
        <h2>{s.organization_name || s.name}</h2>
        <p className="kpi-head__meta">
          <span>
            <Icon name="location" size={15} />
            {[s.outlet_name, s.outlet_city].filter(Boolean).join(' - ') || s.name}
          </span>
          {s.lead_name ? <span>Lead: {s.lead_name}</span> : null}
        </p>
      </div>
      <div className="kpi-head__actions">
        {children}
        <span className={`kpi-pill kpi-pill--${active ? 'live' : status.tone}`}>
          {active ? (
            <>
              <span className="kpi-pill__dot" />
              Floor scans active
            </>
          ) : (
            status.label
          )}
        </span>
      </div>
    </header>
  );
}

/** Live: in progress first, then upcoming (soonest first), then closed (latest first). */
function sortSchedules(list) {
  const rank = { in_progress: 0, not_started: 1, closed: 2 };
  return [...list].sort((a, b) => {
    const ra = rank[a.schedule_status] ?? 1;
    const rb = rank[b.schedule_status] ?? 1;
    if (ra !== rb) return ra - rb;
    if (ra === 1) return new Date(a.start_at) - new Date(b.start_at);
    return new Date(b.end_at) - new Date(a.end_at);
  });
}

function optionLabel(audit) {
  const status = STATUS[audit.schedule_status]?.label || '';
  const who = audit.organization_name || audit.name;
  const where = audit.outlet_name ? ` · ${audit.outlet_name}` : '';
  return `${audit.schedule_no || `#${audit.id}`} (${who}${where}${status ? ` - ${status}` : ''})`;
}

/**
 * KPI board with a schedule picker, for the dashboard and the client home
 * page. Refreshes live when phones upload (and every minute as a fallback).
 */
export function ScheduleKpiBoard({ schedules, storageKey = 'kpiBoardSchedule' }) {
  const sorted = useMemo(() => sortSchedules(schedules || []), [schedules]);
  const [selected, setSelected] = useState(() => {
    try {
      return localStorage.getItem(storageKey) || '';
    } catch {
      return '';
    }
  });
  const auditId = sorted.some((a) => String(a.id) === selected) ? selected : sorted[0] ? String(sorted[0].id) : '';
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!auditId) return;
    try {
      const data = await auditService.getOverview(auditId);
      setOverview({ id: auditId, ...data });
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, [auditId]);

  useEffect(() => {
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  useLiveEvents(auditId ? `/audits/${auditId}/events` : null, load, { debounceMs: 4000 });

  const choose = (value) => {
    setSelected(value);
    try {
      localStorage.setItem(storageKey, value);
    } catch {
      // storage unavailable: the choice lasts until the page is closed
    }
  };

  if (!sorted.length) return null;
  const current = overview && overview.id === auditId ? overview : null;
  const picker = (
    <select
      className="kpi-head__select"
      value={auditId}
      onChange={(event) => choose(event.target.value)}
      aria-label="Schedule"
    >
      {sorted.map((audit) => (
        <option key={audit.id} value={String(audit.id)}>
          {optionLabel(audit)}
        </option>
      ))}
    </select>
  );

  return (
    <section className="kpi-board" aria-label="Schedule KPIs">
      {current?.kpis ? (
        <>
          <KpiHeader kpis={current.kpis}>
            {picker}
            <Link className="kpi-head__open" to={`/audits/${auditId}/portal`}>
              Open portal
            </Link>
          </KpiHeader>
          <KpiCards kpis={current.kpis} progress={current.progress} />
        </>
      ) : (
        <>
          <div className="kpi-board__bar">{picker}</div>
          {error ? (
            <p className="kpi-board__error">Couldn’t load the schedule KPIs: {error}</p>
          ) : (
            <PageLoader label="Loading schedule KPIs…" className="kpi-board__loader" />
          )}
        </>
      )}
    </section>
  );
}

export default ScheduleKpiBoard;
